// src/hooks/useFavorites.js
import { useState, useEffect, useCallback } from 'react';
import supabase from '../../supabase-client';

const useFavorites = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generate unique device ID based on browser fingerprint
  const getDeviceId = useCallback(() => {
    let deviceId = localStorage.getItem('device_id');
    
    if (!deviceId) {
      // Create a unique device ID based on browser characteristics
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
      
      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        screen.width + 'x' + screen.height,
        new Date().getTimezoneOffset(),
        canvas.toDataURL()
      ].join('|');
      
      // Create hash from fingerprint
      let hash = 0;
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      
      deviceId = 'device_' + Math.abs(hash).toString(36);
      localStorage.setItem('device_id', deviceId);
    }
    
    return deviceId;
  }, []);

  // Get device favorites from localStorage
  const getDeviceFavorites = useCallback(() => {
    const deviceId = getDeviceId();
    const favorites = localStorage.getItem(`favorites_${deviceId}`);
    return favorites ? JSON.parse(favorites) : [];
  }, [getDeviceId]);

  // Save device favorites to localStorage
  const saveDeviceFavorites = useCallback((favorites) => {
    const deviceId = getDeviceId();
    localStorage.setItem(`favorites_${deviceId}`, JSON.stringify(favorites));
  }, [getDeviceId]);

  // Check if product is favorited by this device
  const isBookFavorited = useCallback((productId, productType = 'book') => {
    const deviceFavorites = getDeviceFavorites();
    const favoriteKey = `${productType}_${productId}`;
    return deviceFavorites.includes(favoriteKey);
  }, [getDeviceFavorites]);

  // Alias for backward compatibility
  const isProductFavorited = isBookFavorited;

  // Toggle favorite status for any product type
  const toggleFavorite = useCallback(async (productId, productType = 'book') => {
    setLoading(true);
    setError(null);
    
    try {
      const deviceFavorites = getDeviceFavorites();
      const favoriteKey = `${productType}_${productId}`;
      const isFavorited = deviceFavorites.includes(favoriteKey);
      const newFavoriteStatus = !isFavorited;
      
      // Determine table and fields based on product type
      let tableName, idField, favoriteCountField;
      switch (productType) {
        case 'bible':
          tableName = 'biblebook';
          idField = 'id_bible';
          favoriteCountField = 'bible_favorite_count_product';
          break;
        case 'thing':
          tableName = 'thingproduct';
          idField = 'id_thing';
          favoriteCountField = 'thing_favorite_count_product';
          break;
        default: // book
          tableName = 'productbook';
          idField = 'id_product';
          favoriteCountField = 'favorite_count_product';
      }
      
      // Update database: favorite_product and favorite_count_product
      const { data: currentProduct, error: fetchError } = await supabase
        .from(tableName)
        .select(favoriteCountField)
        .eq(idField, productId)
        .single();

      if (fetchError) throw fetchError;

      const currentCount = currentProduct[favoriteCountField] || 0;
      const newCount = newFavoriteStatus ? currentCount + 1 : Math.max(0, currentCount - 1);

      // Prepare update object based on product type
      let updateObj = {};
      if (productType === 'bible') {
        updateObj = {
          bible_favorite_product: newCount > 0,
          bible_favorite_count_product: newCount
        };
      } else if (productType === 'thing') {
        updateObj = {
          thing_favorite_product: newCount > 0,
          thing_favorite_count_product: newCount
        };
      } else {
        updateObj = {
          favorite_product: newCount > 0,
          favorite_count_product: newCount
        };
      }

      const { error: updateError } = await supabase
        .from(tableName)
        .update(updateObj)
        .eq(idField, productId);

      if (updateError) throw updateError;

      // Update local device favorites
      let updatedFavorites;
      if (newFavoriteStatus) {
        updatedFavorites = [...deviceFavorites, favoriteKey];
      } else {
        updatedFavorites = deviceFavorites.filter(key => key !== favoriteKey);
      }
      
      saveDeviceFavorites(updatedFavorites);
      
      return { 
        success: true, 
        isFavorited: newFavoriteStatus,
        favoriteCount: newCount
      };
    } catch (err) {
      const errorMsg = err.message || 'Error al actualizar favoritos';
      setError(errorMsg);
      return { 
        success: false, 
        message: errorMsg,
        isFavorited: isBookFavorited(productId)
      };
    } finally {
      setLoading(false);
    }
  }, [getDeviceFavorites, saveDeviceFavorites, isBookFavorited]);

  // Get all favorite books for this device
  const getFavoriteBooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const deviceFavorites = getDeviceFavorites();
      
      if (deviceFavorites.length === 0) {
        return { success: true, data: [] };
      }

      const { data, error } = await supabase
        .from('productbook')
        .select(`
          *,
          authorsbook (
            name_author
          )
        `)
        .in('id_product', deviceFavorites)
        .eq('available_product', true)
        .order('id_product', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (err) {
      const errorMsg = err.message || 'Error al cargar libros favoritos';
      setError(errorMsg);
      return { success: false, data: [], message: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [getDeviceFavorites]);

  return {
    toggleFavorite,
    isBookFavorited,
    isProductFavorited,
    getFavoriteBooks,
    getDeviceFavorites,
    loading,
    error
  };
};

export default useFavorites;

